import type { HeroSlide } from '@/components/hub/HeroCarousel';
import type { WebResource } from '@/content/resources';
import { resourceForHref, webHrefFromResourceKey } from '@/lib/web-resource-href';

export type FeaturedHeroInput = {
  resource_key: string;
  hero_image_url?: string | null;
  title_override?: string | null;
};

export const FALLBACK_HERO_HREFS = [
  '/app/flashcards/colors',
  '/app/games/match-numbers',
  '/app/storybooks/mai',
  '/app/sheets/useful-terms?terms=words',
] as const;

function toSlide(
  resource: WebResource,
  catalog: WebResource[],
  extras?: { imageUrl?: string | null; title?: string },
): HeroSlide | null {
  if (!webHrefFromResourceKey(resource.href, catalog)) return null;
  return {
    href: resource.href,
    title: extras?.title || resource.title,
    category: resource.category,
    imageUrl: extras?.imageUrl,
    premium: resource.premium,
  };
}

export function slideFromFeaturedItem(item: FeaturedHeroInput, resources: WebResource[]): HeroSlide | null {
  const href = webHrefFromResourceKey(item.resource_key, resources);
  if (!href) return null;
  const resource = resourceForHref(href, resources);
  if (!resource) return null;
  return toSlide(resource, resources, {
    imageUrl: item.hero_image_url,
    title: item.title_override || undefined,
  });
}

/** Featured API first, dropping mobile-only keys. If nothing remains, use web fallbacks. */
export function buildWebHeroSlides(
  featured: FeaturedHeroInput[],
  resources: WebResource[],
  fallbackHrefs: readonly string[] = FALLBACK_HERO_HREFS,
): HeroSlide[] {
  const fromApi = featured
    .map((item) => slideFromFeaturedItem(item, resources))
    .filter((slide): slide is HeroSlide => slide !== null);
  if (fromApi.length) return fromApi;
  return fallbackHrefs.flatMap((href) => {
    const resource = resourceForHref(href, resources);
    return resource ? toSlide(resource, resources) ?? [] : [];
  });
}
